import {useEffect, useState} from "react";
import {useSearchParams} from "react-router-dom";
import {fetchWrapper} from "../../../../_services/fetch";
import {config} from "../../../../utils2";
import * as yup from "yup";
import {useAuth} from "../../../../context";

const api = config.get_remote_api() + "/api/tip"

const endpointOrderOptions = () => `${api}/order`
const endpointDetails = (userId, tipId) => `${api}/${tipId}`
const endpointCount = (userId, search) => `${api}/count${fetchWrapper.getSearchParams(search)}`
const endpointList = (userId, search) => `${api}/list${fetchWrapper.getSearchParams(search)}`

const endpointAdd = () => `${api}/add`
const endpointEdit = (userId, targetId) => `${api}/${targetId}`
const endpointDelete = (userId, targetId) => `${api}/${targetId}`

const msg = "Failed with no error messages from the server."
const handleSuccess = resp => resp.success ? Promise.resolve(resp?.data) : Promise.reject({message: resp.message || msg})
const handleError = err => err?.name !== "AbortError" ? err?.error || err?.message || msg : null

const defaultValues = {
    id: null,
    title: "",
    description: "",
    url: "",
    media_first_path: "",
    medias: [],
    tags: [],
    products: [],
    created_at: null,
    updated_at: null,
}

export function useTip(userId, tiid = null) {

    const validationSchema = yup.object().shape({
        title: yup.string().required("Title is required"),
        url: yup.string().required("Url is required"),
        description: yup.string().required("description is required"),
        tags: yup.array().of(yup.object().shape({
            id: yup.string().uuid(),
            name: yup.string().required(),
            variant: yup.string().required(),
        })),
        medias: yup.array().of(yup.object().shape({
            id: yup.string().uuid(),
        })).min(1, "Minimum 1 picture"),
    })


    const {token} = useAuth()
    const {value, setValue, loadingValue, setLoadingValue, errorValue, setErrorValue} = useTipDetails(tiid, defaultValues)

    function handleSuccessInt(resp) {
        if (resp.success) {
            setValue(resp?.data ? resp.data : defaultValues);
            return Promise.resolve(resp.message);
        } else {
            return Promise.reject({message: resp?.message || "Fetch failed with no error messages from the server."});
        }
    }

    const handleErrorInt = err => {
        setErrorValue(handleError(err))
        return Promise.reject(handleError(err))
    }

    function addItem(formValue) {
        return fetchWrapper.post(endpointAdd(userId), formValue, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function editItem(formValue, targetId = tiid) {
        return fetchWrapper.put(endpointEdit(userId, targetId), formValue, token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function deleteItem(targetId = tiid) {
        return fetchWrapper.delete(endpointDelete(userId, targetId), token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    return {
        value,
        validationSchema,
        addItem,
        editItem,
        deleteItem,
        loadingValue,
        errorValue,
        clearErrorValue: () => setErrorValue(null)
    }
}

export function useTipDetails(tipId, defaultValue) {

    const {userId} = useAuth()
    const [loadingValue, setLoadingValue] = useState(false);
    const [errorValue, setErrorValue] = useState(null);
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        const controller = new AbortController()

        if (tipId && tipId !== "add") {
            setErrorValue(null)
            setLoadingValue(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointDetails(userId, tipId), requestOptions)
                .then(handleSuccess)
                .then(setValue)
                .catch(err => setErrorValue(handleError(err)))
                .finally(() => setLoadingValue(false))
        }

        return () => {
            controller.abort()
            setLoadingValue(false)
        }
    }, [tipId]);

    return {
        value,
        setValue,
        loadingValue,
        setLoadingValue,
        errorValue,
        setErrorValue,
        clearError: () => setErrorValue(null)
    }
}

export function useTipCount(queryOption = null) {

    const {userId} = useAuth()
    const [searchParams] = useSearchParams()
    const [loadingCount, setLoadingCount] = useState(false);
    const [errorCount, setErrorCount] = useState(null);
    const [count, setCount] = useState(0);

    // Search options
    const query = queryOption || searchParams.get("query")

    useEffect(() => {
        const controller = new AbortController()

        setErrorCount(null)
        setLoadingCount(true)

        // Fetch options
        const requestOptions = {
            method: "GET",
            signal: controller.signal,
        }

        // Search params
        const search = {query}

        fetchWrapper.generic(endpointCount(userId, search), requestOptions)
            .then(handleSuccess)
            .then(setCount)
            .catch(err => setErrorCount(handleError(err)))
            .finally(() => setLoadingCount(false))

        return () => {
            controller.abort()
            setLoadingCount(false)
        }
    }, [query]);

    return {
        count,
        setCount,
        loadingCount,
        errorCount,
        clearErrorCount: () => setErrorCount(null)
    }
}


export function useTipList(pageSizeOption = null, pageIndexOption = null, queryOption = null, orderByOption = null, keepPrev = false) {

    const defaultPageSize = 25
    const defaultPageIndex = 0

    const {userId} = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loadingList, setLoadingList] = useState(false);
    const [errorList, setErrorList] = useState(null);
    const [list, setList] = useState([]);
    const [reload, setReload] = useState(0);

    // Search options
    const pageSize = pageSizeOption || Number(searchParams.get("size")) || defaultPageSize
    const pageIndex = pageIndexOption || Number(searchParams.get("index")) || defaultPageIndex
    const query = queryOption || searchParams.get("query")
    const orderBy = orderByOption || searchParams.get("by")

    useEffect(() => {
        const controller = new AbortController()

        if (pageIndex != null && pageSize != null) {
            setErrorList(null)
            setLoadingList(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                signal: controller.signal,
            }

            // Search params
            const search = {
                offset: pageIndex * pageSize,
                limit: pageSize,
                query: query,
                order_by: orderBy
            }

            fetchWrapper.generic(endpointList(userId, search), requestOptions)
                .then(handleSuccess)
                .then(data => setList(prev => keepPrev ? ([...prev, ...data]) : data))
                .catch(err => setErrorList(handleError(err)))
                .finally(() => setLoadingList(false))
        }

        return () => {
            controller.abort()
            setLoadingList(false)
        }
    }, [pageIndex, pageSize, query, orderBy, reload]);

    return {
        list,
        setList: fields => {
            setList(prev => {
                const fieldsNew = (typeof fields === "function") ? fields(prev) : fields
                if (fieldsNew.length === 0 && pageIndex > 0) {
                    setSearchParams(prev => {
                        prev.set("index", `${pageIndex - 1}`)
                    })
                } else if (fieldsNew.length < prev.length) {
                    setReload(reload + 1)
                }
                return [...fieldsNew]
            })
        },
        loadingList,
        errorList,
        clearErrorList: () => setErrorList(null)
    }
}

export function useTipOrderOptions(defaultOptions = []) {

    const {loggedIn, userId, token} = useAuth()
    const [loadingOrderOptions, setLoadingOrderOptions] = useState(false);
    const [errorOrderOptions, setErrorOrderOptions] = useState(null);
    const [orderOptions, setOrderOptions] = useState(defaultOptions);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorOrderOptions(null)
            setLoadingOrderOptions(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointOrderOptions(userId), requestOptions)
                .then(handleSuccess)
                .then(setOrderOptions)
                .catch(err => setErrorOrderOptions(handleError(err)))
                .finally(() => setLoadingOrderOptions(false))
        }

        return () => {
            controller.abort()
            setLoadingOrderOptions(false)
        }
    }, [loggedIn]);


    return {
        orderOptions,
        setOrderOptions,
        loadingOrderOptions,
        setLoadingOrderOptions,
        errorOrderOptions,
        setErrorOrderOptions,
        clearErrorOrderOptions: () => setErrorOrderOptions(null)
    }
}